import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Loader2, UserRound, Handshake, AlertTriangle, CheckCircle2, Trash2, Plus, Info,
} from 'lucide-react';
import { Button } from '../ui-v2';
import { fetchTiposContrato } from '../../../peticiones_API/PeticionesGestionPersonasPersonas';
import { Modal, Input, Select, StepIndicator } from '../ui-v2.5';
import {
  listNegociosCore, listPuestos, listPerfiles, getPerfil, listRoles, listOficinas,
  listAreas, listRecursos, resolverDestinatarios, crearAltaIncorp, comprobarDniIncorp,
  getSolicitud, descargarDocumentoDni, listEmpresasColaboradoras, listEmpresasContratantes,
  alcanceMio,
} from './api';
// Mismo mecanismo de CP → provincia/población que usa el alta de empleado (FormCrearPersona).
import { buscarDireccionPorCP } from '../../../utils/direccionesUtils';
// El mapa de iconos de recurso sale a módulo compartido en Fase 10: lo usa también el panel de detalle.
import { IconoRecurso } from './iconosRecurso';
import SelectorResponsable from './components/SelectorResponsable';
import { activosDelAmbito, ambitoDe, COLABORADOR } from './pertenencia';

// Envoltorio de "dato aportado por el candidato" (tratamiento azul del catálogo). Inerte si `on` es
// falso (camino manual no prellena nada; §4.6 lo activa por campo).
function Prefilled({ on, children }) {
  if (!on) return children;
  return (
    <div className="rounded-v25-md border-l-2 border-blue-300 bg-blue-50/60 pl-2 pt-1">
      <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-blue-700">Dato del candidato</span>
      {children}
    </div>
  );
}

/**
 * WizardAlta — creación de un alta (Fase 9). CINCO pasos: datos personales · empresa y puesto ·
 * material y tareas · avisos y áreas · revisar y crear.
 *
 * El paso "Tipo" del mockup YA NO EXISTE (§4.7): el tipo de persona nunca se pregunta aquí porque
 * siempre se sabe antes de abrir el wizard —
 *   · desde solicitud → `solicitud.tipo_persona` (se creó como interno o como colaborador);
 *   · manual          → la pestaña activa del listado, que llega en la prop `tipo`.
 * Llega fijado y sin opción de cambiarlo: preguntarlo otra vez permitía contradecir el origen.
 *
 * Dos caminos con el mismo wizard: MANUAL (solicitud=null) y DESDE SOLICITUD (§4.6, prellenado).
 * Si la creación falla, el modal NO se cierra ni pierde lo escrito (el usuario corrige y reintenta).
 */

const PASOS = ['Datos personales', 'Empresa y puesto', 'Material y tareas', 'Avisos y áreas', 'Revisar'];
const cuerpoError = (e) => (e && e.response && e.response.data) || {};
const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Dominio corporativo de los INTERNOS. Los colaboradores NO lo llevan: son de otra empresa y su
// correo no es @grupolae.net (§4.7 B). Mientras no haya dónde configurar el dominio de cada empresa
// colaboradora, para ellos se sugiere solo `nombre.apellido` y RRHH escribe el dominio que toque.
const DOMINIO_INTERNO = 'grupolae.net';


// Sugerencia de correo derivada del nombre (RRHH la acepta o la cambia). Campo libre.
// `dominio` vacío → devuelve `nombre.apellido` sin arroba.
function sugerirMail(nombre, apellidos, dominio) {
  const limpia = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z]/g, '');
  const n = limpia(nombre);
  const a = limpia((apellidos || '').trim().split(/\s+/)[0]);
  if (!n || !a) return '';
  return dominio ? `${n}.${a}@${dominio}` : `${n}.${a}`;
}

export default function WizardAlta({ open, onClose, onCreada, solicitud = null, tipo: tipoManual = 'interno' }) {
  const [paso, setPaso] = useState(1);
  const [cat, setCat] = useState({ negocios: [], puestos: [], perfiles: [], roles: [], departamentos: [], oficinas: [], areas: [], recursos: [], empresasColab: [], empresas: [], tiposContrato: [] });
  const [cargandoCat, setCargandoCat] = useState(true);
  // TIPO DERIVADO, nunca estado editable: la solicitud manda; si no hay, la pestaña activa.
  const tipo = solicitud && solicitud.tipo_persona ? solicitud.tipo_persona : tipoManual;
  const [persona, setPersona] = useState({
    nombre: '', apellidos: '', dni: '', mail_empresa: '', telefono_personal: '',
    direccion: '', codigo_postal: '', poblacion: '', provincia: '', lugar_nacimiento: '', fecha_nacimiento: '', direccion_teletrabajo: '',
  });
  const [mailTocado, setMailTocado] = useState(false);
  const [alta, setAlta] = useState({ negocio_id: '', departamento_id: '', oficina_id: '', puesto_id: '', perfil_id: '', empresa_contratante_id: '', tipo_contrato: '', fecha_incorporacion: '', cargo: '', empresa_colaboradora_id: '' });
  const [recursos, setRecursos] = useState([]);   // [{recurso_id, nombre}]
  const [tareas, setTareas] = useState([]);        // [{descripcion, area_id, responsable}]
  // Responsable DIRECTO del alta (Fase 10, decisión 5). El paso del mockup existía y Fase 9 lo
  // perdió, dejando `incorp_altas.responsable_id` siempre a null: sin él el panel no tiene la caja
  // de contexto ni a quién avisar de las credenciales. Objeto {id, nombre} o null.
  const [responsable, setResponsable] = useState(null);
  const [addRecursoId, setAddRecursoId] = useState('');
  const [areasSel, setAreasSel] = useState(new Set());
  const [destPreview, setDestPreview] = useState(null);
  const [destCargando, setDestCargando] = useState(false);
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState(null);       // { code, message, persona_id? }
  const [dniRaw, setDniRaw] = useState(null);      // { dni, en_personas, en_colaboradoras }
  const [dniChecking, setDniChecking] = useState(false);
  // Desde solicitud (§4.6): campos prellenados (azul), resumen de datos aportados, y fotos del DNI.
  const [prefillSet, setPrefillSet] = useState(new Set());
  const [datosResumen, setDatosResumen] = useState(null); // { iban, num_seguridad_social, documentos, hijos, titulaciones }
  const [dniFotos, setDniFotos] = useState({ anverso: null, reverso: null });
  const esPre = (campo) => prefillSet.has(campo);

  // Catálogos al abrir
  useEffect(() => {
    if (!open) return undefined;
    let cancel = false;
    setCargandoCat(true);
    Promise.all([listNegociosCore(), listPuestos(), listPerfiles(), listRoles(), listOficinas(), listAreas(), listRecursos(), listEmpresasColaboradoras(), listEmpresasContratantes(), fetchTiposContrato()])
      .then(([ns, ps, pf, rl, of, ar, rc, ec, em, tc]) => {
        if (cancel) return;
        const arr = (x, k) => (Array.isArray(x) ? x : (x && (x[k] || x.data)) || []);
        // Negocios del core (con Departamentos anidados) → {id, nombre, departamentos:[{id,nombre}]}
        const negocios = arr(ns, 'negocios').map((n) => ({
          id: n.Id ?? n.id, nombre: n.nombre,
          departamentos: (n.Departamentos || []).map((d) => ({ id: d.Id ?? d.id, nombre: d.nombre })),
        }));
        setCat({
          negocios, puestos: arr(ps), perfiles: arr(pf), roles: arr(rl, 'roles'),
          oficinas: arr(of, 'oficinas'), areas: arr(ar), recursos: arr(rc),
          // Array plano ordenado por nombre; se normaliza el id (PK `Id` en mayúscula, como negocios).
          empresasColab: arr(ec, 'empresas').map((e) => ({ id: e.Id ?? e.id, nombre: e.nombre })),
          // Razones sociales y tipos de contrato: los dos PEDIDOS, no escritos aqui.
          empresas: arr(em, 'empresas').map((e) => ({ id: e.Id ?? e.id, nombre: e.nombre })),
          tiposContrato: Array.isArray(tc) ? tc : [],
        });
      })
      .catch(() => { if (!cancel) toast.error('No se pudieron cargar los catálogos.'); })
      .finally(() => { if (!cancel) setCargandoCat(false); });
    return () => { cancel = true; };
  }, [open]);

  // Mail sugerido mientras RRHH no lo toque
  useEffect(() => {
    if (mailTocado) return;
    // Interno → dominio corporativo. Colaborador → sin dominio (no es @grupolae.net).
    const s = sugerirMail(persona.nombre, persona.apellidos, tipo === 'interno' ? DOMINIO_INTERNO : '');
    if (s) setPersona((p) => ({ ...p, mail_empresa: s }));
  }, [persona.nombre, persona.apellidos, mailTocado, tipo]);

  // DESDE SOLICITUD (§4.6): prellenar persona con lo NO confidencial, marcar los campos como "del
  // candidato" (azul), guardar el resumen de datos aportados, y cargar las fotos del DNI. El DNI NO se
  // prellena (el formulario público no lo recoge). El mail tampoco (es personal → se deriva del nombre).
  useEffect(() => {
    if (!open || !solicitud) return undefined;
    let cancel = false;
    const urls = [];
    getSolicitud(solicitud.id).then((d) => {
      if (cancel) return;
      // Estado comprobado AL ABRIR, no solo al enviar: si el listado venía desactualizado (otra
      // pestaña ya completó el alta), se avisa aquí en vez de dejar rellenar cinco pasos para
      // fallar con un 409 al final. `alta_id` es el hecho; el estado, el paso del flujo.
      //
      // LAS MISMAS CUATRO RAMAS QUE crearAltaService, en el mismo orden y con el mismo significado.
      // Antes eran tres: 'completada' sin alta_id caía en el cubo de "no ha enviado sus datos" y
      // el aviso mentía sobre lo que pasaba (una fila heredada NO es un candidato sin datos).
      if (d.alta_id) {
        setError({ code: 'solicitud_ya_completada', message: 'Esta solicitud ya generó un alta. Recarga el listado.' });
      } else if (d.estado === 'cancelada') {
        setError({ code: 'solicitud_cancelada', message: 'Esta solicitud está cancelada.' });
      } else if (d.estado === 'completada') {
        setError({
          code: 'solicitud_estado_inconsistente',
          message: 'Esta solicitud está marcada como completada pero no tiene alta asociada (dato heredado). Hay que corregir su estado a "recibida" antes de completarla.',
        });
      } else if (d.estado !== 'recibida') {
        setError({ code: 'solicitud_sin_datos', message: 'El candidato todavía no ha enviado sus datos.' });
      }

      const det = d.datos_detalle || {};
      const pre = new Set();
      setPersona((p) => {
        const np = { ...p };
        // Nombre y apellidos van SEPARADOS en `personas`, y la solicitud ya los trae así desde
        // §4.7. Las anteriores a la columna `apellidos_candidato` (null) guardaban el nombre
        // completo en un campo: para ESAS, y solo para esas, se parte por el primer espacio y
        // queda editable — el heurístico es un apaño para lo heredado, no el camino normal.
        if (d.nombre_candidato) {
          if (d.apellidos_candidato) {
            np.nombre = d.nombre_candidato; pre.add('nombre');
            np.apellidos = d.apellidos_candidato; pre.add('apellidos');
          } else {
            const trozos = String(d.nombre_candidato).trim().split(/\s+/);
            np.nombre = trozos[0] || ''; pre.add('nombre');
            if (trozos.length > 1) { np.apellidos = trozos.slice(1).join(' '); pre.add('apellidos'); }
          }
        }
        ['direccion', 'codigo_postal', 'poblacion', 'provincia', 'direccion_teletrabajo', 'lugar_nacimiento', 'fecha_nacimiento', 'telefono_personal']
          .forEach((k) => { if (det[k] != null && det[k] !== '') { np[k] = det[k]; pre.add(k); } });
        return np;
      });
      setPrefillSet(pre);
      setDatosResumen(d.datos_resumen || null);
    }).catch(() => { if (!cancel) toast.error('No se pudo cargar la solicitud.'); });
    // Fotos del DNI (cada cara puede o no existir)
    ['anverso', 'reverso'].forEach((cara) => {
      descargarDocumentoDni(solicitud.id, cara)
        .then((blob) => { if (cancel) return; const u = URL.createObjectURL(blob); urls.push(u); setDniFotos((f) => ({ ...f, [cara]: u })); })
        .catch(() => {});
    });
    return () => { cancel = true; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [open, solicitud]);

  const setP = (k) => (e) => setPersona((p) => ({ ...p, [k]: e.target.value }));
  const setA = (k) => (e) => setAlta((x) => ({ ...x, [k]: e.target.value }));

  // Teléfono personal: SOLO dígitos (se descarta cualquier otro carácter al teclear/pegar).
  const setTelefono = (e) => {
    const soloDigitos = String(e.target.value || '').replace(/\D/g, '');
    setPersona((p) => ({ ...p, telefono_personal: soloDigitos }));
  };

  // Código postal → provincia y población automáticas, con el MISMO mecanismo que el alta de
  // empleado (`buscarDireccionPorCP` de utils/direccionesUtils, sobre el JSON de códigos postales).
  // Con 5 dígitos y CP conocido rellena los dos campos; siguen siendo editables a mano después.
  const setCodigoPostal = (e) => {
    const cp = String(e.target.value || '').replace(/\D/g, '').slice(0, 5);
    setPersona((p) => {
      const np = { ...p, codigo_postal: cp };
      if (cp.length === 5) {
        const dir = buscarDireccionPorCP(cp);
        if (dir) { np.provincia = dir.provincia; np.poblacion = dir.poblacion; }
      }
      return np;
    });
  };

  // Derivado del check de DNI, POR TIPO: tabla destino → bloquea; la otra → avisa y permite.
  // Se recalcula solo si el DNI comprobado sigue siendo el del formulario (si cambia, queda obsoleto).
  const dniInfo = useMemo(() => {
    if (!dniRaw || dniRaw.dni !== persona.dni.trim()) return null;
    const destino = tipo === 'interno' ? dniRaw.en_personas : dniRaw.en_colaboradoras;
    const otra = tipo === 'interno' ? dniRaw.en_colaboradoras : dniRaw.en_personas;
    return {
      bloqueo: destino.existe ? destino.persona : null,
      aviso: otra.existe ? { nombre: otra.persona.nombre, tabla: tipo === 'interno' ? 'colaboradoras' : 'personas' } : null,
    };
  }, [dniRaw, tipo, persona.dni]);

  async function comprobarDni() {
    const dni = persona.dni.trim();
    if (!dni) { setDniRaw(null); return; }
    if (dniRaw && dniRaw.dni === dni) return; // ya comprobado
    setDniChecking(true);
    try { const res = await comprobarDniIncorp(dni); setDniRaw(res); }
    catch (_) { /* si falla el check, el backend sigue de backstop al crear */ }
    finally { setDniChecking(false); }
  }

  /* -- DE DONDE CUELGAN LOS PUESTOS DE ESTA ALTA ------------------------------------------
   *
   * De un alta de INTERNO, del negocio. De una de COLABORADOR, de su empresa colaboradora: desde
   * el 02/10 un puesto de colaborador tiene `negocio_id` a NULL, asi que el filtro de antes
   * -`p.negocio_id === Number(alta.negocio_id)`- los dejaba fuera a TODOS sin dar ningun error.
   * Se creaba el puesto, se creaba el perfil, y aqui no aparecian. */
  const ambito = useMemo(
    () => ambitoDe(tipo, { negocioId: alta.negocio_id, colaboradoraId: alta.empresa_colaboradora_id }),
    [tipo, alta.negocio_id, alta.empresa_colaboradora_id],
  );
  /* El padre tiene que estar elegido para que haya algo que ofrecer, y cual es depende del tipo. */
  const padreElegido = tipo === COLABORADOR ? !!alta.empresa_colaboradora_id : !!alta.negocio_id;

  /* ── MI ALCANCE: QUE PUESTOS PUEDO USAR, Y POR QUE ──────────────────────────────────────
   *
   * Quien tiene puestos asignados solo puede dar de alta EN ELLOS, y el servidor lo rechaza con
   * 403 si llega otro. Acotar aqui el desplegable evita ese rechazo —pero acotarlo SIN DECIRLO
   * es peor que no acotarlo: quien ve cuatro puestos donde hay treinta no piensa «estoy
   * acotada», piensa «falta el catalogo», y eso acaba en una incidencia a Sistemas.
   *
   * Por eso se guarda `restringido` y no solo la lista: la linea que lo explica depende de
   * saber que ESTAMOS acotados, no de contar cuantos han quedado. Con cero vinculos la lista
   * tambien viene vacia, y significa lo contrario. */
  const [alcance, setAlcance] = useState({
    restringido: false, puestos: [], tipos: [], negocios: [], colaboradoras: [],
  });
  useEffect(() => {
    let cancel = false;
    alcanceMio()
      .then((a) => {
        if (cancel) return;
        setAlcance({
          restringido: !!a.restringido,
          puestos: a.puestos || [],
          tipos: a.tipos || [],
          negocios: a.negocios || [],
          colaboradoras: a.colaboradoras || [],
        });
      })
      .catch((e) => {
        /* Si esto falla NO se acota: dejar el desplegable completo y que el servidor rechace es
         * mejor que esconder puestos por un fallo de red y hacer creer que no existen. El
         * servidor sigue siendo quien decide; esto solo ahorra el viaje. */
        console.error('[incorp][wizard] alcanceMio', e);
      });
    return () => { cancel = true; };
  }, []);

  /* ── UNA SOLA OPCION NO ES UNA PREGUNTA ────────────────────────────────────────────────
   *
   * Si el alcance deja una unica empresa colaboradora o un unico puesto, un desplegable es una
   * pregunta cuya respuesta ya se sabe. Se ensena como DATO.
   *
   * El umbral es UNA, no «pocas»: con dos ya hay una decision que tomar, y convertirla en dato
   * obligaria a elegir por el usuario. Y si se admitiera «dos tambien», alguien preguntaria por
   * que no tres.
   *
   * Dos cosas que lo hacen seguro:
   *   · el VALOR viaja igual —se autorrellena—, asi que es presentacion y no logica: el resto
   *     del wizard no tiene que saber de este caso;
   *   · y se DICE por que es el unico. Sin eso, el dia que esa persona tenga dos, vera aparecer
   *     un desplegable donde habia un texto y parecera que la pantalla cambio sola. */
  const UnicaOpcion = ({ label, valor, porque }) => (
    <div>
      <span className="block text-[13px] font-medium text-v25-ink-700">{label}</span>
      <span className="mt-1 block text-sm text-v25-ink-900">{valor}</span>
      <span className="mt-0.5 block text-xs text-v25-ink-400">{porque}</span>
    </div>
  );

  const idsPermitidos = useMemo(
    () => new Set((alcance.puestos || []).map((p) => p.id)),
    [alcance.puestos],
  );

  /* ── EL RECORTE SUBE: NO SOLO LOS PUESTOS ──────────────────────────────────────────────
   *
   * Acotar solo el desplegable de puestos dejaba llegar a el por caminos que no llevan a
   * ninguna parte: elegir un negocio donde no tiene ninguno, o una empresa colaboradora que no
   * es la suya, y encontrarse la lista vacia. El desplegable estaba bien; el camino hasta el,
   * no.
   *
   * Los tres ejes se DERIVAN de sus puestos en el servidor —de sus puestos salen sus empresas y
   * sale que tipos puede dar—, asi que no hay dos listas que puedan contradecirse. */
  const negociosPermitidos = useMemo(() => {
    if (!alcance.restringido) return cat.negocios;
    const ids = new Set(alcance.negocios || []);
    return cat.negocios.filter((n) => ids.has(Number(n.id)));
  }, [cat.negocios, alcance.restringido, alcance.negocios]);

  const colabsPermitidas = useMemo(() => {
    if (!alcance.restringido) return cat.empresasColab;
    const ids = new Set(alcance.colaboradoras || []);
    return cat.empresasColab.filter((e) => ids.has(Number(e.id)));
  }, [cat.empresasColab, alcance.restringido, alcance.colaboradoras]);

  /* El valor se rellena solo cuando no hay nada que elegir. Se hace en un efecto y no al pintar
   * para que el estado del formulario sea el mismo que si lo hubiera elegido a mano: lo que
   * cambia es la pregunta, no lo que se guarda. */
  const unicaColab = alcance.restringido && tipo === COLABORADOR && colabsPermitidas.length === 1
    ? colabsPermitidas[0] : null;
  useEffect(() => {
    if (unicaColab && String(alta.empresa_colaboradora_id) !== String(unicaColab.id)) {
      cambiarColaboradora(String(unicaColab.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unicaColab]);

  const puestosNeg = useMemo(() => {
    const delAmbito = activosDelAmbito(cat.puestos, ambito);
    return alcance.restringido ? delAmbito.filter((p) => idsPermitidos.has(p.id)) : delAmbito;
  }, [cat.puestos, ambito, alcance.restringido, idsPermitidos]);

  /* ── VA DESPUES DE `puestosNeg`, Y ESTUVO ANTES ────────────────────────────────────────
   *
   * Dejo la pantalla EN BLANCO al abrir el wizard: `const` tiene zona muerta temporal, asi que
   * leer `puestosNeg` siete lineas por encima de su declaracion lanza «Cannot access before
   * initialization» al montar.
   *
   * Es la SEGUNDA vez en dos dias, y las dos por lo mismo: insertar un valor DERIVADO encima de
   * aquello de lo que deriva. Un valor derivado va siempre debajo de su fuente, sin excepciones
   * —agruparlo «con los de su tema» es lo que lo sube—.
   *
   * Y la primera vez di por limpia la comprobacion que lo cazaba porque no imprimio nada, sin
   * mirar su codigo de salida. */
  const unicoPuesto = alcance.restringido && puestosNeg.length === 1 ? puestosNeg[0] : null;
  useEffect(() => {
    if (unicoPuesto && String(alta.puesto_id) !== String(unicoPuesto.id)) {
      cambiarPuesto(String(unicoPuesto.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unicoPuesto]);

  const perfilesNeg = useMemo(
    () => activosDelAmbito(cat.perfiles, ambito).filter((p) => !alta.puesto_id || p.puesto_id === Number(alta.puesto_id)),
    [cat.perfiles, ambito, alta.puesto_id],
  );
  const departamentosNeg = useMemo(() => (cat.negocios.find((n) => n.id === Number(alta.negocio_id)) || {}).departamentos || [], [cat.negocios, alta.negocio_id]);
  const areasActivas = useMemo(() => cat.areas.filter((a) => a.activa), [cat.areas]);
  const recursosActivos = useMemo(() => cat.recursos.filter((r) => r.activo), [cat.recursos]);

  function cambiarNegocio(v) {
    const deps = (cat.negocios.find((n) => n.id === Number(v)) || {}).departamentos || [];
    // El departamento se conserva solo si pertenece al nuevo negocio; si no, se limpia.
    setAlta((x) => ({ ...x, negocio_id: v, puesto_id: '', perfil_id: '', departamento_id: deps.some((d) => d.id === Number(x.departamento_id)) ? x.departamento_id : '' }));
    setRecursos([]); setTareas([]); setDestPreview(null);
  }
  /* Cambiar de empresa colaboradora cambia QUE PUESTOS existen, exactamente igual que cambiar de
   * negocio. Si no se limpian, el puesto elegido antes se queda puesto y ya no pertenece a nada. */
  function cambiarColaboradora(v) {
    setAlta((x) => ({ ...x, empresa_colaboradora_id: v, puesto_id: '', perfil_id: '' }));
    setRecursos([]); setTareas([]);
  }
  function cambiarPuesto(v) {
    setAlta((x) => ({ ...x, puesto_id: v, perfil_id: '' }));
    const pu = cat.puestos.find((p) => p.id === Number(v));
    if (pu && !alta.cargo) setAlta((x) => ({ ...x, cargo: pu.nombre }));
  }

  // Al elegir perfil → prellenar recursos, tareas y la razon social (editables)
  async function cambiarPerfil(v) {
    setAlta((x) => ({ ...x, perfil_id: v }));
    if (!v) return;
    try {
      const d = await getPerfil(v);
      setRecursos((d.recursos || []).map((r) => ({ recurso_id: r.id, nombre: r.nombre, icono: r.icono })));
      // La plantilla del perfil NO trae responsable: `incorp_perfil_tareas` no tiene esa columna.
      // Se deja a null y se elige aquí, tarea por tarea, si hace falta.
      setTareas((d.tareas || []).map((t) => ({ descripcion: t.descripcion, area_id: t.area_id, responsable: null })));
      // La razon social que el perfil DECLARA: se prellena y se puede cambiar aqui.
      // Declarar y no deducir — la razon social NO se infiere del negocio.
      if (d.empresa_contratante_id) {
        setAlta((x) => ({ ...x, empresa_contratante_id: String(d.empresa_contratante_id) }));
      }
    } catch (_) { toast.error('No se pudo cargar el perfil.'); }
  }

  // Resolver destinatarios / áreas por defecto al entrar en el paso 5
  const resolver = useCallback(async () => {
    if (!alta.negocio_id) return;
    setDestCargando(true);
    try {
      const d = await resolverDestinatarios(Number(alta.negocio_id), alta.puesto_id ? Number(alta.puesto_id) : null);
      setDestPreview(d);
      const areasDef = [...new Set((d.destinatarios || []).flatMap((x) => (x.motivos || []).filter((m) => m.via === 'area' && m.area).map((m) => m.area.id)))];
      setAreasSel(new Set(areasDef));
    } catch (_) { setDestPreview({ error: true }); }
    finally { setDestCargando(false); }
  }, [alta.negocio_id, alta.puesto_id]);

  useEffect(() => { if (open && paso === 4 && !destPreview) resolver(); }, [open, paso, destPreview, resolver]);

  const toggleArea = (id) => setAreasSel((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  const quitarRecurso = (rid) => setRecursos((prev) => prev.filter((r) => r.recurso_id !== rid));
  const anadirRecurso = () => {
    const id = Number(addRecursoId);
    if (!id || recursos.some((r) => r.recurso_id === id)) return;
    const rc = recursosActivos.find((r) => r.id === id);
    if (rc) setRecursos((prev) => [...prev, { recurso_id: rc.id, nombre: rc.nombre, icono: rc.icono }]);
    setAddRecursoId('');
  };
  // Tareas: quitar / añadir / editar descripción y área responsable.
  const quitarTarea = (i) => setTareas((prev) => prev.filter((_, idx) => idx !== i));
  const anadirTarea = () => setTareas((prev) => [...prev, { descripcion: '', area_id: null, responsable: null }]);
  const editarTarea = (i, campo, valor) => setTareas((prev) => prev.map((t, idx) => (idx === i ? { ...t, [campo]: valor } : t)));

  // Validación mínima por paso (no bloquea navegar atrás; sí avanzar sin lo obligatorio).
  // Numeración de 5 pasos: 1 datos personales · 2 empresa y puesto · 3 material · 4 avisos · 5 revisar.
  function puedeAvanzar() {
    if (paso === 1) {
      // OJO con `dniChecking`: NO entra aquí, y es a propósito (Fase 10).
      //
      // Estaba, y hacía que el PRIMER clic en "Siguiente" se perdiera siempre. El check de DNI va en
      // el `onBlur` del campo; al pulsar el botón, el clic hace blur → arranca `comprobarDni()` →
      // `setDniChecking(true)` → este guard pasa a falso → React deshabilita el botón ANTES de que
      // corra su `onClick`. El usuario veía que pulsaba y no ocurría nada, y solo avanzaba al segundo
      // clic. Reproducido de forma determinista.
      //
      // La comprobación previa es una COMODIDAD (adelantar lo que el backend ya valida), no una
      // barrera. La barrera es el RESULTADO —`dniInfo.bloqueo`, es decir, que el DNI exista en la
      // tabla destino—, y eso sí sigue aquí. Y como el resultado puede llegar después de avanzar, el
      // botón de crear también lo comprueba (ver el pie del wizard), así que el bloqueo no se puede
      // sortear pasando de paso rápido. Detrás de todo queda `crearAltaService`, que responde 409
      // `dni_duplicado` por su cuenta.
      return persona.nombre.trim() && persona.apellidos.trim() && persona.dni.trim()
        && RE_EMAIL.test(persona.mail_empresa.trim()) && !(dniInfo && dniInfo.bloqueo);
    }
    // La empresa colaboradora es obligatoria SOLO en el camino colaborador: la persona pertenece a
    // una de ellas y sin ese dato queda mal creada (el backend también la exige).
    /* El padre que corresponde al tipo, no los dos: pedir negocio a un colaborador era lo que
       bloqueaba su camino entero. */
    if (paso === 2) return tipo === COLABORADOR ? !!alta.empresa_colaboradora_id : !!alta.negocio_id;
    return true;
  }

  function construirPayload() {
    return {
      tipo_persona: tipo,
      solicitud_id: solicitud ? solicitud.id : undefined,
      persona: {
        nombre: persona.nombre.trim(), apellidos: persona.apellidos.trim(), dni: persona.dni.trim(),
        mail_empresa: persona.mail_empresa.trim(), telefono_personal: persona.telefono_personal || null,
        direccion: persona.direccion || null, codigo_postal: persona.codigo_postal || null,
        poblacion: persona.poblacion || null, provincia: persona.provincia || null,
        lugar_nacimiento: persona.lugar_nacimiento || null, fecha_nacimiento: persona.fecha_nacimiento || null,
        direccion_teletrabajo: persona.direccion_teletrabajo || null,
      },
      alta: {
        /* El servidor lo descarta por el tipo igualmente; no mandarlo es decir lo mismo en los
           dos sitios en vez de confiar en que el otro limpie. */
        negocio_id: tipo === COLABORADOR ? null : Number(alta.negocio_id),
        departamento_id: alta.departamento_id ? Number(alta.departamento_id) : null,
        oficina_id: alta.oficina_id ? Number(alta.oficina_id) : null,
        puesto_id: alta.puesto_id ? Number(alta.puesto_id) : null,
        perfil_id: alta.perfil_id ? Number(alta.perfil_id) : null,
        // `rol_sistema` no se manda: el servidor lo decide y lo ignoraria igualmente.
        tipo_contrato: alta.tipo_contrato || null,
        /* 'sin' —«autonomo / sin empresa contratante»— viaja TAL CUAL. `Number('sin')` seria
           NaN, y convertirlo a null aqui lo haria indistinguible de no haber elegido: justo lo
           que esta opcion existe para separar. El servidor lo traduce a NULL. */
        empresa_contratante_id: alta.empresa_contratante_id === 'sin'
          ? 'sin'
          : (alta.empresa_contratante_id ? Number(alta.empresa_contratante_id) : null),
        responsable_id: responsable ? responsable.id : null,
        fecha_incorporacion: alta.fecha_incorporacion || null, cargo: alta.cargo || null,
        // Solo tiene sentido para colaboradores; para internos se manda null explícito.
        empresa_colaboradora_id: tipo === 'colaborador' && alta.empresa_colaboradora_id ? Number(alta.empresa_colaboradora_id) : null,
      },
      recursos: recursos.map((r) => ({ recurso_id: r.recurso_id })),
      tareas: tareas
        .filter((t) => (t.descripcion || '').trim())
        .map((t, i) => ({ descripcion: t.descripcion.trim(), area_id: t.area_id || null, responsable_id: t.responsable ? t.responsable.id : null, orden: i })),
      areas: [...areasSel],
    };
  }

  async function crear() {
    setCreando(true); setError(null);
    try {
      const r = await crearAltaIncorp(construirPayload());
      toast.success(`Alta creada: ${r.numero}.`);
      if (r.aviso_cruce) {
        toast.info(`Nota: ya existía ${r.aviso_cruce.persona} con ese DNI en ${r.aviso_cruce.existe_en}. Se ha creado como ${tipo} nuevo.`, { autoClose: 8000 });
      }
      if (r.notificacion) {
        const n = r.notificacion;
        toast.info(n.ok ? `Aviso compuesto para ${n.destinatarios} destinatario(s).` : 'El alta se creó pero no se pudo notificar (queda registro).');
      }
      if (onCreada) onCreada();
      onClose();
    } catch (e) {
      // El modal NO se cierra ni pierde lo escrito: mostramos el error y el usuario reintenta.
      const b = cuerpoError(e);
      setError({ code: b.error, message: b.message || 'No se pudo crear el alta.', persona_id: b.persona_id });
      toast.error(b.message || 'No se pudo crear el alta.');
    } finally {
      setCreando(false);
    }
  }

  const ultimo = paso === PASOS.length;
  const footer = (
    <div className="flex w-full items-center justify-between">
      <Button variant="outline" onClick={() => setPaso((p) => Math.max(1, p - 1))} disabled={paso === 1 || creando}>Atrás</Button>
      {ultimo ? (
        // `dniInfo.bloqueo` también aquí: al quitar `dniChecking` de puedeAvanzar (ver el comentario
        // de esa función), el paso 1 puede quedar atrás antes de que el check responda. Comprobarlo
        // en el botón de crear cierra ese hueco —a estas alturas el resultado ya está— y deja la
        // comprobación previa como lo que es: una comodidad, no una barrera de navegación.
        <Button onClick={crear} disabled={creando || !alta.fecha_incorporacion
          || (tipo === COLABORADOR ? !alta.empresa_colaboradora_id : (!alta.negocio_id || !alta.empresa_contratante_id))
          || !!(dniInfo && dniInfo.bloqueo)}>
          {creando ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}Crear alta
        </Button>
      ) : (
        <Button onClick={() => setPaso((p) => Math.min(PASOS.length, p + 1))} disabled={!puedeAvanzar()}>Siguiente</Button>
      )}
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} size="xl" title={solicitud ? `Completar alta · ${solicitud.nombre_candidato}` : 'Nueva incorporación'}
      subtitle={`Paso ${paso} de ${PASOS.length}: ${PASOS[paso - 1]}`} footer={footer}>
      {cargandoCat ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-v25-ink-500"><Loader2 className="h-5 w-5 animate-spin" /> Cargando…</div>
      ) : (
        <div className="space-y-5">
          <StepIndicator variant="bars" steps={PASOS} current={paso} />

          {error && (
            <div className="flex items-start gap-2 rounded-v25-md border border-v25-expired-bd bg-v25-expired-bg p-3 text-sm text-v25-expired-fg">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error.message}</span>
            </div>
          )}

          {/* PASO 1 · Datos personales */}
          {paso === 1 && (
            <div className="space-y-4">
              {/* Tipo: fijado por el origen (solicitud o pestaña activa). Se muestra, no se elige. */}
              <div className="flex items-center gap-2 rounded-v25-md border border-v25-line bg-v25-surface-sunken px-3 py-2 text-sm">
                {tipo === 'interno' ? <UserRound className="h-4 w-4 text-v25-ink-500" /> : <Handshake className="h-4 w-4 text-v25-ink-500" />}
                <span className="text-v25-ink-800">Se creará como <strong className="font-medium">{tipo === 'interno' ? 'interno' : 'colaborador'}</strong></span>
                <span className="ml-auto text-[11px] text-v25-ink-400">
                  {solicitud ? 'según la solicitud' : 'según la pestaña activa'}
                </span>
              </div>

              {/* Aviso: qué aporta la solicitud (presencia, no valores) */}
              {solicitud && datosResumen && (
                <div className="flex items-start gap-2 rounded-v25-md border border-blue-200 bg-blue-50/60 p-3 text-xs text-blue-900">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-medium">Datos recibidos del candidato — los campos en azul vienen del formulario.</p>
                    <p className="mt-0.5">La solicitud aporta además: {[
                      datosResumen.iban && 'IBAN',
                      datosResumen.num_seguridad_social && 'nº Seguridad Social',
                      datosResumen.hijos && `${datosResumen.hijos} hijo(s)`,
                      datosResumen.titulaciones && `${datosResumen.titulaciones} titulación(es)`,
                      datosResumen.documentos && `${datosResumen.documentos} foto(s) de DNI`,
                    ].filter(Boolean).join(' · ') || 'ningún dato adicional'}. (IBAN y nº SS se cifran y no se muestran aquí.)</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Prefilled on={esPre('nombre')}><Input label="Nombre *" value={persona.nombre} onChange={setP('nombre')} maxLength={150} /></Prefilled>
                <Prefilled on={esPre('apellidos')}><Input label="Apellidos *" value={persona.apellidos} onChange={setP('apellidos')} maxLength={150} /></Prefilled>
                <div>
                  <Input label="DNI / NIE *" value={persona.dni}
                    onChange={(e) => { setDniRaw(null); setP('dni')(e); }} onBlur={comprobarDni}
                    maxLength={15} error={dniInfo && dniInfo.bloqueo ? 'Ya existe en la intranet' : undefined}
                    hint={dniChecking ? 'Comprobando…' : 'Transcríbelo mirando las fotos del DNI.'} />
                  {dniInfo && dniInfo.bloqueo && (
                    <p className="mt-1 text-xs text-red-600">
                      Ya existe {tipo === 'interno' ? 'una persona' : 'un colaborador'} con este DNI: <strong>{dniInfo.bloqueo.nombre}</strong>. No se puede crear.
                    </p>
                  )}
                  {dniInfo && dniInfo.aviso && (
                    <p className="mt-1 flex items-start gap-1 text-xs text-v25-received-fg">
                      <Info className="mt-0.5 h-3 w-3 shrink-0" />
                      Existe en {dniInfo.aviso.tabla} (<strong>{dniInfo.aviso.nombre}</strong>): es la misma persona cambiando de relación. Puedes continuar.
                    </p>
                  )}
                </div>
                <Input label="Email corporativo *" value={persona.mail_empresa}
                  onChange={(e) => { setMailTocado(true); setP('mail_empresa')(e); }}
                  hint={tipo === 'interno'
                    ? 'Sugerido del nombre; puedes cambiarlo.'
                    : 'Sugerido sin dominio: completa el de la empresa colaboradora.'} />
                <Prefilled on={esPre('telefono_personal')}>
                  <Input label="Teléfono personal" value={persona.telefono_personal} onChange={setTelefono}
                    inputMode="numeric" maxLength={20} hint="Solo dígitos." />
                </Prefilled>
                <Prefilled on={esPre('fecha_nacimiento')}><Input label="Fecha de nacimiento" type="date" value={persona.fecha_nacimiento} onChange={setP('fecha_nacimiento')} /></Prefilled>
              </div>

              {/* Dirección — lugar de nacimiento vive aquí, no en datos personales (§4.7) */}
              <section className="space-y-3 border-t border-v25-line pt-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Dirección</h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2"><Prefilled on={esPre('direccion')}><Input label="Dirección" value={persona.direccion} onChange={setP('direccion')} maxLength={255} /></Prefilled></div>
                  <Prefilled on={esPre('codigo_postal')}>
                    <Input label="Código postal" value={persona.codigo_postal} onChange={setCodigoPostal}
                      inputMode="numeric" maxLength={5} hint="Rellena provincia y población." />
                  </Prefilled>
                  <Prefilled on={esPre('poblacion')}><Input label="Población" value={persona.poblacion} onChange={setP('poblacion')} maxLength={120} /></Prefilled>
                  <Prefilled on={esPre('provincia')}><Input label="Provincia" value={persona.provincia} onChange={setP('provincia')} maxLength={120} /></Prefilled>
                  <Prefilled on={esPre('lugar_nacimiento')}><Input label="Lugar de nacimiento" value={persona.lugar_nacimiento} onChange={setP('lugar_nacimiento')} maxLength={120} /></Prefilled>
                  <div className="sm:col-span-2"><Prefilled on={esPre('direccion_teletrabajo')}><Input label="Dirección de teletrabajo" value={persona.direccion_teletrabajo} onChange={setP('direccion_teletrabajo')} maxLength={255} /></Prefilled></div>
                </div>
              </section>

              {/* Fotos del DNI aportadas (para transcribir el número) */}
              {(dniFotos.anverso || dniFotos.reverso) && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-v25-ink-700">Fotos del DNI aportadas (para transcribir el número)</label>
                  <div className="flex flex-wrap gap-3">
                    {dniFotos.anverso && <img src={dniFotos.anverso} alt="DNI anverso" className="h-28 rounded-v25-md border border-v25-line object-contain" />}
                    {dniFotos.reverso && <img src={dniFotos.reverso} alt="DNI reverso" className="h-28 rounded-v25-md border border-v25-line object-contain" />}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PASO 2 · Empresa y puesto */}
          {paso === 2 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {/* ── NEGOCIO Y DEPARTAMENTO: SOLO PARA INTERNO ────────────────────────────
                  Un colaborador no trabaja para una marca, trabaja para su empresa
                  colaboradora; y un departamento cuelga de un negocio, asi que tampoco.
                  Hasta el 03/10 el wizard pedia las dos cosas en el camino de colaborador, y
                  `incorp_altas.negocio_id` era NOT NULL, asi que el alta se veia obligada a
                  declarar una marca que no significaba nada. El servidor las descarta ahora por
                  el TIPO; aqui dejan de pedirse. */}
              {tipo !== COLABORADOR && (
                <>
                  <Select label="Negocio *" value={alta.negocio_id} onChange={(e) => cambiarNegocio(e.target.value)}
                    hint={alcance.restringido
                      ? 'Ves los negocios en los que puedes dar de alta.'
                      : 'La marca para la que va a trabajar. No es con quién firma el contrato.'}>
                    <option value="">Selecciona…</option>
                    {negociosPermitidos.map((n) => <option key={n.id} value={n.id}>{n.nombre}</option>)}
                  </Select>
                  <Select label="Departamento" value={alta.departamento_id} onChange={setA('departamento_id')} disabled={!alta.negocio_id}
                    hint={alta.negocio_id && departamentosNeg.length === 0 ? 'Este negocio no tiene departamentos.' : undefined}>
                    <option value="">—</option>
                    {departamentosNeg.map((dp) => <option key={dp.id} value={dp.id}>{dp.nombre}</option>)}
                  </Select>
                </>
              )}
              {/* Solo camino COLABORADOR: a qué empresa colaboradora pertenece la persona. Va a
                  `personas_colaboradoras.empresa_colaboradora_id`. Los internos no lo tienen.
                  VA ANTES DEL PUESTO, y no es cosmética: desde el 02/10 los puestos de colaborador
                  cuelgan de ESTA empresa, así que mientras no esté elegida el desplegable de
                  puestos no tiene nada que ofrecer. Estaba debajo, y con el filtro ya arreglado
                  habría seguido saliendo vacío — un orden de pantalla puede anular un arreglo. */}
              {tipo === COLABORADOR && unicaColab && (
                <UnicaOpcion label="Empresa colaboradora" valor={unicaColab.nombre}
                  porque="Es tu única empresa asignada." />
              )}
              {tipo === COLABORADOR && !unicaColab && (
                <Select label="Empresa colaboradora *" value={alta.empresa_colaboradora_id} onChange={(e) => cambiarColaboradora(e.target.value)}
                  hint={colabsPermitidas.length === 0
                    ? (alcance.restringido
                      ? 'No tienes puestos en ninguna empresa colaboradora.'
                      : 'No hay empresas colaboradoras dadas de alta.')
                    : (alcance.restringido
                      ? 'Ves las empresas en las que puedes dar de alta.'
                      : 'La empresa a la que pertenece la persona. Decide qué puestos puedes elegir.')}>
                  <option value="">Selecciona…</option>
                  {colabsPermitidas.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                </Select>
              )}
              {unicoPuesto && (
                <UnicaOpcion label="Puesto" valor={unicoPuesto.nombre}
                  porque="Es tu único puesto asignado." />
              )}
              {!unicoPuesto && (
              <Select label="Puesto" value={alta.puesto_id} onChange={(e) => cambiarPuesto(e.target.value)} disabled={!padreElegido}
                hint={(() => {
                  /* El orden de los mensajes importa: si esta acotada, eso explica la lista
                     ANTES que cualquier otra cosa. «No hay puestos aqui» y «no hay puestos
                     TUYOS aqui» mandan a sitios distintos —uno a crear un puesto, otro a
                     pedir que te asignen— y confundirlos cuesta una incidencia. */
                  if (!padreElegido) return undefined;
                  if (alcance.restringido) {
                    return puestosNeg.length === 0
                      ? 'No tienes asignado ningún puesto aquí. Pide a RRHH que te asigne uno.'
                      : 'Ves los puestos en los que puedes dar de alta. Si falta alguno, pide a RRHH que te lo asigne.';
                  }
                  if (puestosNeg.length === 0) {
                    return tipo === COLABORADOR
                      ? 'Esta empresa colaboradora no tiene puestos. Créalos en Configuración › Puestos.'
                      : 'Este negocio no tiene puestos.';
                  }
                  return undefined;
                })()}>
                <option value="">—</option>
                {puestosNeg.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </Select>
              )}
              <Select label="Perfil (prellena material y tareas)" value={alta.perfil_id} onChange={(e) => cambiarPerfil(e.target.value)} disabled={!padreElegido}
                hint={padreElegido && perfilesNeg.length === 0 ? 'Sin perfiles para este puesto.' : undefined}>
                <option value="">—</option>
                {/* Un perfil NO tiene nombre: ES la combinacion puesto + tipo, y asi lo garantiza
                    `uq_incorp_perfiles_padre_puesto` desde el 02/10 (antes entraba ademas el
                    negocio; ahora el padre lo pone el puesto). Se etiquetaba por su rol, que dejo
                    de existir: sin esto todos dirian «Perfil 4». */}
                {perfilesNeg.map((p) => {
                  const pu = cat.puestos.find((x) => (x.id ?? x.Id) === p.puesto_id);
                  const et = [pu && pu.nombre, p.tipo_persona === COLABORADOR ? 'colaborador' : 'interno']
                    .filter(Boolean).join(' · ');
                  return <option key={p.id} value={p.id}>{et || `Perfil ${p.id}`}</option>;
                })}
              </Select>
              <Select label="Oficina" value={alta.oficina_id} onChange={setA('oficina_id')}>
                <option value="">—</option>
                {cat.oficinas.map((o) => <option key={o.Id || o.id} value={o.Id || o.id}>{o.nombre}</option>)}
              </Select>
              <Input label="Cargo" value={alta.cargo} onChange={setA('cargo')} maxLength={100} />
              {/* EL ROL DEL SISTEMA YA NO SE ELIGE AQUI (Fase C).
                  Todas las altas nacen como «General» y Sistemas lo cambia despues si hace falta,
                  asi que este desplegable ofrecia una decision que no existe: la unica respuesta
                  correcta estaba puesta por defecto y cualquier otra era un error.
                  EL VALOR NO DESAPARECE, cambia de sitio: lo pone el SERVIDOR, en
                  `crearAltaService.resolverRolId`, que ahora ignora lo que llegue en la peticion.
                  Quitarlo de la pantalla no habria sido quitarlo del servicio. */}
              {/* LA RAZON SOCIAL CON LA QUE SE CONTRATA.
                  Obligatoria, y el porque es el motivo de esta fase: hoy 271 personas la tienen
                  vacia y RRHH las rellena una a una, pero cada alta nueva nacia sin ella. El numero
                  bajaba con cada ficha rellenada y subia con cada alta: las dos cosas corrian en
                  direcciones contrarias.
                  El perfil la PRELLENA si la declara, y se puede cambiar. No se deduce del negocio:
                  34 personas trabajan para dos o mas marcas con una sola razon social.
                  Solo para internos: un colaborador se contrata a traves de su empresa
                  colaboradora, que ya se pide arriba. */}
              {tipo !== 'colaborador' && (
                <Select label="Empresa contratante *" value={alta.empresa_contratante_id}
                  onChange={setA('empresa_contratante_id')}
                  hint="Razón social con la que firma. No es la marca para la que trabaja.">
                  <option value="">Seleccionar…</option>
                  {/* EXPLICITA, NO OBLIGATORIA. Un autonomo no tiene razon social contratante
                      porque no la hay; pero dejar el hueco libre haria que «es autonomo» y «se me
                      olvido» no se distinguieran nunca mas. El boton de crear sigue sin dejar
                      avanzar en blanco: lo que cambia es que ahora hay algo que elegir. */}
                  <option value="sin">Autónomo / sin empresa contratante</option>
                  {cat.empresas.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                </Select>
              )}
              {/* Desplegable, no caja de texto: los tipos los fija el Estatuto y se piden por API,
                  asi que esta pantalla no lleva ni un valor escrito. */}
              <Select label="Tipo de contrato" value={alta.tipo_contrato} onChange={setA('tipo_contrato')}
                hint="Modalidad con la que entra. Se puede cambiar después en su ficha.">
                <option value="">Sin especificar</option>
                {cat.tiposContrato.map((t) => <option key={t.clave} value={t.clave}>{t.etiqueta}</option>)}
              </Select>
              {/* OBLIGATORIA: es la que decide cuando esta persona pasa a «Alta» y puede entrar.
                  Sin ella nace en «En proceso» y no la activa nadie — el cron mira esta fecha. */}
              <Input label="Fecha de incorporación *" type="date" value={alta.fecha_incorporacion}
                onChange={setA('fecha_incorporacion')}
                hint="Cuándo empieza. Si es futura, no podrá entrar hasta ese día." />
              {/* Responsable DIRECTO de la persona (Fase 10, decisión 5). Ocupa las dos columnas: el
                  autocomplete despliega resultados y con media anchura se queda estrecho. */}
              <div className="sm:col-span-2">
                <SelectorResponsable
                  valor={responsable}
                  onChange={setResponsable}
                  label="Responsable directo"
                  placeholderVacio="Quien recibe el aviso de credenciales y aparece como responsable en el panel de seguimiento."
                />
              </div>
            </div>
          )}

          {/* PASO 3 · Material y tareas */}
          {paso === 3 && (
            <div className="space-y-6">
              {/* Material — tarjetas con icono */}
              <section className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Material a preparar</h4>
                <p className="text-[11px] text-v25-ink-400">Prellenado del perfil; puedes quitar o añadir.</p>
                {recursos.length === 0 ? <p className="text-sm text-v25-ink-400">Sin material asignado.</p> : (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {recursos.map((r) => (
                      <div key={r.recurso_id} className="flex items-center gap-2 rounded-v25-md border border-v25-line bg-white px-3 py-2">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-v25-md bg-v25-surface-sunken text-v25-ink-600">
                          <IconoRecurso nombre={r.icono} className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm text-v25-ink-800">{r.nombre}</span>
                        <button type="button" onClick={() => quitarRecurso(r.recurso_id)} className="text-v25-ink-400 hover:text-red-600" aria-label="Quitar"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Select label="Añadir recurso" value={addRecursoId} onChange={(e) => setAddRecursoId(e.target.value)}>
                      <option value="">Selecciona…</option>
                      {recursosActivos.filter((r) => !recursos.some((x) => x.recurso_id === r.id)).map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                    </Select>
                  </div>
                  <Button variant="outline" onClick={anadirRecurso} disabled={!addRecursoId}><Plus />Añadir</Button>
                </div>
              </section>

              {/* Tareas — editables (descripción + área responsable) */}
              <section className="space-y-2 border-t border-v25-line pt-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Tareas de onboarding</h4>
                  <Button variant="outline" size="sm" onClick={anadirTarea}><Plus />Añadir tarea</Button>
                </div>
                <p className="text-[11px] text-v25-ink-400">El área responsable recibe el aviso con su tarea. La persona es opcional y se puede afinar después desde el panel de seguimiento.</p>
                {tareas.length === 0 ? <p className="text-sm text-v25-ink-400">Sin tareas.</p> : (
                  <div className="space-y-2">
                    {tareas.map((t, i) => (
                      <div key={i} className="flex items-start gap-2 rounded-v25-md border border-v25-line bg-white p-2">
                        <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-3">
                          <Input label="Descripción" value={t.descripcion} onChange={(e) => editarTarea(i, 'descripcion', e.target.value)} maxLength={200} />
                          <Select label="Área responsable" value={t.area_id || ''} onChange={(e) => editarTarea(i, 'area_id', e.target.value ? Number(e.target.value) : null)}>
                            <option value="">Sin área (responsable directo)</option>
                            {areasActivas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                          </Select>
                          {/* Persona concreta, opcional y distinta del área: el panel la muestra por
                              tarea. La plantilla del perfil no la trae (esa columna no existe allí). */}
                          <SelectorResponsable
                            valor={t.responsable}
                            onChange={(p) => editarTarea(i, 'responsable', p)}
                            label="Persona (opcional)"
                          />
                        </div>
                        <button type="button" onClick={() => quitarTarea(i)} className="mt-6 shrink-0 text-v25-ink-400 hover:text-red-600" aria-label="Quitar tarea"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}

          {/* PASO 4 · Avisos y áreas */}
          {paso === 4 && (
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-v25-ink-700">Áreas a avisar (por defecto, las de las reglas; ajusta si hace falta)</label>
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {areasActivas.map((a) => (
                    <label key={a.id} className="flex cursor-pointer items-center gap-2 rounded-v25-md border border-v25-line px-2.5 py-1.5 hover:bg-v25-surface-sunken">
                      <input type="checkbox" checked={areasSel.has(a.id)} onChange={() => toggleArea(a.id)} className="h-4 w-4" />
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: a.color || '#94a3b8' }} />
                      <span className="truncate text-sm text-v25-ink-800">{a.nombre}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="rounded-v25-md border border-v25-line bg-v25-surface-sunken p-3">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-v25-ink-600"><Info className="h-3.5 w-3.5" /> A quién se avisará (resuelto)</div>
                {destCargando ? <p className="text-xs text-v25-ink-500">Calculando…</p>
                  : destPreview?.error ? <p className="text-xs text-red-600">No se pudo calcular.</p>
                  : destPreview ? (
                    (destPreview.destinatarios || []).length === 0
                      ? <p className="text-xs text-v25-ink-500">Ninguna regla avisa a nadie para esta combinación.</p>
                      : <div className="flex flex-wrap gap-1">{destPreview.destinatarios.map((d) => <span key={d.id} className="rounded-v25-full bg-white px-2 py-0.5 text-xs text-v25-ink-700 ring-1 ring-v25-line">{d.nombre}</span>)}</div>
                  ) : <p className="text-xs text-v25-ink-400">—</p>}
              </div>
            </div>
          )}

          {/* PASO 5 · Revisar */}
          {paso === 5 && (
            <div className="space-y-3 text-sm">
              {dniInfo && dniInfo.aviso && (
                <div className="flex items-start gap-2 rounded-v25-md border border-v25-received-bd bg-v25-received-bg p-3 text-xs text-v25-received-fg">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Recuerda: ya existe <strong>{dniInfo.aviso.nombre}</strong> con este DNI en {dniInfo.aviso.tabla}. Se creará como {tipo} nuevo (no se vincula).</span>
                </div>
              )}
              <div className="rounded-v25-md border border-v25-line bg-white p-4">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Se va a crear</h4>
                <p><strong>{persona.nombre} {persona.apellidos}</strong> · {tipo} · DNI {persona.dni}</p>
                <p className="text-v25-ink-600">{persona.mail_empresa}</p>
                {tipo === 'colaborador' && (
                  <p className="mt-1 text-v25-ink-600">
                    Empresa colaboradora: <strong className="font-medium">
                      {(cat.empresasColab.find((e) => e.id === Number(alta.empresa_colaboradora_id)) || {}).nombre || '—'}
                    </strong>
                  </p>
                )}
                <p className="mt-1 text-v25-ink-600">
                  {(cat.negocios.find((n) => n.id === Number(alta.negocio_id)) || {}).nombre || '—'}
                  {alta.puesto_id ? ` · ${(puestosNeg.find((p) => p.id === Number(alta.puesto_id)) || {}).nombre || ''}` : ''}
                  {alta.fecha_incorporacion ? ` · incorporación ${alta.fecha_incorporacion}` : ''}
                </p>
                <p className="mt-1 text-xs text-v25-ink-500">{recursos.length} recurso(s) · {tareas.length} tarea(s) · {areasSel.size} área(s) a avisar · responsable: {responsable ? responsable.nombre : 'sin asignar'}</p>
              </div>
              <div className="rounded-v25-md border border-v25-line bg-v25-surface-sunken p-3">
                <div className="mb-1 text-xs font-semibold text-v25-ink-600">Se avisará a</div>
                {destPreview && (destPreview.destinatarios || []).length
                  ? <div className="flex flex-wrap gap-1">{destPreview.destinatarios.map((d) => <span key={d.id} className="rounded-v25-full bg-white px-2 py-0.5 text-xs text-v25-ink-700 ring-1 ring-v25-line">{d.nombre}</span>)}</div>
                  : <p className="text-xs text-v25-ink-500">Nadie (según las reglas y áreas elegidas).</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
